import React from 'react';
import { Space, Typography, Row, Col, Divider, Empty, Tag } from 'antd';
import {
  ClockCircleOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { ObservabilityData } from './types';
import { HealthBadge } from './HealthBadge';
import { MetricCard } from './MetricCard';
import { MetricsTimeline } from './MetricsTimeline';
import moment from 'moment';

const { Text, Title } = Typography;

interface ObservabilityViewerProps {
  data: ObservabilityData;
}

/**
 * ObservabilityViewer - Main container for observability metrics display
 *
 * This component is designed to be self-contained and easily extractable
 * for use in other parts of the application.
 */
export const ObservabilityViewer: React.FC<ObservabilityViewerProps> = ({ data }) => {
  if (!data || !data.metrics || Object.keys(data.metrics).length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No observability data collected yet"
      />
    );
  }

  const {
    metrics,
    slo_results = {},
    history = {},
    overall_health = 'healthy',
    last_updated,
    target_keys = []
  } = data;

  // Separate metrics by widget type for layout
  const statMetrics: string[] = [];
  const timelineMetrics: string[] = [];

  Object.entries(metrics).forEach(([key, metric]) => {
    const widget = metric.display_hints?.widget || 'stat_card';
    if (widget === 'line_chart' || metric.value_type === 'time_series') {
      // Check if we have history data for this metric
      if (history?.[key] && history[key].length > 0) {
        timelineMetrics.push(key);
      } else {
        statMetrics.push(key);
      }
    } else {
      statMetrics.push(key);
    }
  });

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Last Updated Header */}
        {last_updated && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <HealthBadge status={overall_health} />
            <Tag icon={<ClockCircleOutlined />} color="default">
              Last updated: {moment(last_updated).format('YYYY-MM-DD HH:mm:ss')}
            </Tag>
          </div>
        )}

        {/* Metric Cards Grid */}
        {statMetrics.length > 0 && (
          <div>
            <Text strong style={{ fontSize: '13px', color: '#666', marginBottom: '12px', display: 'block' }}>
              Current Metrics
            </Text>
            <Row gutter={[16, 16]}>
              {statMetrics.map(key => {
                const metric = metrics[key];
                const sloResult = slo_results?.[key];
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={key}>
                    <MetricCard
                      metricKey={key}
                      metric={metric}
                      sloResult={sloResult}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* Timeline Charts */}
        {timelineMetrics.length > 0 && (
          <div>
            <Divider style={{ margin: '8px 0 16px 0' }} />
            <Space style={{ marginBottom: '12px' }}>
              <LineChartOutlined style={{ color: '#1890ff' }} />
              <Text strong style={{ fontSize: '13px', color: '#666' }}>
                Metrics Over Time
              </Text>
            </Space>
            <Row gutter={[16, 16]}>
              {timelineMetrics.map(key => {
                const metric = metrics[key];
                const metricHistory = history[key] || [];
                return (
                  <Col xs={24} lg={12} key={key}>
                    <MetricsTimeline
                      metricKey={key}
                      title={metric.display_hints?.title || metric.name}
                      history={metricHistory}
                      displayHints={metric.display_hints}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* SLO Summary (if any SLOs are defined) */}
        {slo_results && Object.keys(slo_results).length > 0 && (
          <div>
            <Divider style={{ margin: '8px 0 16px 0' }} />
            <Text strong style={{ fontSize: '13px', color: '#666', marginBottom: '8px', display: 'block' }}>
              SLO Status
            </Text>
            <Space wrap size={[8, 8]}>
              {Object.entries(slo_results).map(([key, slo]) => {
                const metric = metrics[key];
                const title = metric?.display_hints?.title || key;
                const statusColors: Record<string, string> = {
                  passing: '#52c41a',
                  warning: '#faad14',
                  error: '#ff4d4f',
                  info: '#1890ff'
                };

                // Safely format values for display
                const formatValue = (val: any): string => {
                  if (val === null || val === undefined) return '-';
                  if (typeof val === 'string' || typeof val === 'number') return String(val);
                  if (Array.isArray(val)) return `[${val.length} items]`;
                  if (typeof val === 'object') return `{${Object.keys(val).length} fields}`;
                  return String(val);
                };

                return (
                  <div
                    key={key}
                    style={{
                      padding: '8px 12px',
                      background: '#fafafa',
                      borderRadius: '6px',
                      border: `1px solid ${statusColors[slo.status] || '#d9d9d9'}`,
                      borderLeftWidth: '3px'
                    }}
                  >
                    <Text style={{ fontSize: '12px' }}>
                      <Text strong>{title}</Text>
                      <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                      <span style={{ color: statusColors[slo.status] }}>
                        {slo.status.toUpperCase()}
                      </span>
                      {slo.current_value !== null && (
                        <>
                          <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                          <Text type="secondary">
                            {formatValue(slo.current_value)} {slo.operator} {formatValue(slo.threshold)}
                          </Text>
                        </>
                      )}
                    </Text>
                  </div>
                );
              })}
            </Space>
          </div>
        )}
      </Space>
    </div>
  );
};

export default ObservabilityViewer;
